import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Clock, Ticket, X, Maximize2 } from "lucide-react";
import { api, wpImg } from "@/lib/api";
import MarkdownView from "@/components/MarkdownView";

export default function EventDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [now, setNow] = useState(new Date());
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setE(r.data)).catch(() => setNotFound(true));
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [id]);

  // Disable body scroll when lightbox open
  useEffect(() => {
    document.body.style.overflow = lightbox ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  const countdown = useMemo(() => {
    if (!e) return null;
    const diff = new Date(e.start_date) - now;
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return { days, hrs, mins };
  }, [e, now]);

  if (notFound) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center">
      <h1 className="font-serif text-4xl text-ink">Event not found</h1>
      <Link to="/events" className="mt-6 text-navy underline">All events</Link>
    </div>;
  }
  if (!e) return <div className="min-h-[60vh]" />;

  const d = new Date(e.start_date);
  const endD = e.end_date ? new Date(e.end_date) : null;
  const fmtDate = (date) => date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "America/New_York" });
  const fmtTime = (date) => date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" });

  return (
    <div data-testid="event-detail-page">
      <section className="bg-navy text-cream py-12 md:py-20 px-5 md:px-10 relative overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-25" />
        <div className="relative max-w-6xl mx-auto">
          <Link to="/events" className="inline-flex items-center gap-2 text-cream/80 hover:text-gold text-sm mb-5">
            <ArrowLeft size={14} /> All events
          </Link>
          <h1 className="display text-4xl md:text-6xl text-cream max-w-4xl">{e.title}</h1>
          <div className="mt-5 flex flex-wrap gap-5 text-sm text-cream/80">
            <span className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> {fmtDate(d)} · {fmtTime(d)}</span>
            {e.location && <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> {e.location}</span>}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-5 md:px-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {e.image_url && (
              <button
                onClick={() => setLightbox(true)}
                data-testid="event-flyer-btn"
                className="group relative w-full rounded-3xl overflow-hidden bg-sand/40 border border-sand block"
                aria-label="View full flyer"
              >
                <img src={wpImg(e.image_url)} alt={e.title} className="w-full h-auto max-h-[80vh] object-contain bg-sand/30" />
                <div className="absolute top-4 right-4 rounded-full bg-navy/85 text-cream backdrop-blur px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 size={12} /> View full
                </div>
              </button>
            )}
          </div>
          <div className="lg:col-span-5 space-y-6">
            {e.description && (
              <p className="font-serif text-xl text-ink leading-relaxed italic">{e.description}</p>
            )}
            {e.long_description && (
              <div className="text-ink/80 leading-relaxed font-light">
                <MarkdownView>{e.long_description}</MarkdownView>
              </div>
            )}
            <aside className="rounded-3xl bg-white border border-sand p-6 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">When</div>
                <div className="mt-1.5 flex items-start gap-3">
                  <Calendar size={16} className="text-navy mt-1" />
                  <div>
                    <div className="font-medium text-ink text-sm">{fmtDate(d)}</div>
                    <div className="text-xs text-ink/60 flex items-center gap-1.5 mt-0.5"><Clock size={11} /> {fmtTime(d)}{endD ? ` – ${fmtDate(endD)}` : ""}</div>
                  </div>
                </div>
              </div>
              {e.location && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Where</div>
                  <div className="mt-1.5 flex items-start gap-3">
                    <MapPin size={16} className="text-navy mt-1" />
                    <div className="text-sm text-ink">{e.location}</div>
                  </div>
                </div>
              )}
              {countdown && (
                <div className="rounded-2xl bg-navy text-cream p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Starts in</div>
                  <div className="mt-2 flex gap-3">
                    <div><div className="font-serif text-3xl">{countdown.days}</div><div className="text-[10px] uppercase text-cream/60">days</div></div>
                    <div><div className="font-serif text-3xl">{countdown.hrs}</div><div className="text-[10px] uppercase text-cream/60">hrs</div></div>
                    <div><div className="font-serif text-3xl">{countdown.mins}</div><div className="text-[10px] uppercase text-cream/60">min</div></div>
                  </div>
                </div>
              )}
              {e.ticket_url && (
                <a href={e.ticket_url} target="_blank" rel="noreferrer" data-testid="event-register-btn"
                   className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-gold text-navy px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-gold-600 transition-colors">
                  <Ticket size={14} /> {e.button_label || "Register / Tickets"}
                </a>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && e.image_url && (
        <div
          className="fixed inset-0 z-50 bg-navy/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(false)}
          data-testid="flyer-lightbox"
        >
          <button onClick={() => setLightbox(false)} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center" aria-label="Close">
            <X size={20} />
          </button>
          <img
            src={wpImg(e.image_url)}
            alt={e.title}
            onClick={(ev) => ev.stopPropagation()}
            className="max-w-full max-h-full w-auto h-auto object-contain rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
